/**
 * `savedDecisionsService` — local-only store for Strategic Decision documents
 * the user generates from the Strategic Decision Partner.
 *
 *   - Documents are auto-saved to `localStorage` the moment they're produced,
 *     so every completed decision shows up in History without an extra click.
 *   - Each row carries an `addedToResources` flag; only documents the user
 *     explicitly promotes appear on the Resources page.
 *   - Storage is keyed per `user_id` so shared browsers never mix accounts.
 */
import markdownit from "markdown-it";
import type { Resource } from "@/types/resource";
import pathChoicePhoto from "@/assets/resources/path-choice.jpg";
import compassPhoto from "@/assets/resources/compass-direction.jpg";

const STORAGE_PREFIX = "heybodhi:saved-decisions:v2:";
const LEGACY_STORAGE_KEY = "heybodhi:saved-decisions:v1";
export const savedDecisionsChangedEvent = "heybodhi:saved-decisions-changed";

const md = markdownit({ html: false, linkify: true, typographer: true });

export interface SavedDecision {
  id: string;
  title: string;
  excerpt: string;
  markdown: string;
  /** ISO timestamp. */
  createdAt: string;
  /** Set when the user explicitly promotes the doc onto the Resources page. */
  addedToResources?: boolean;
}

interface SaveDecisionInput {
  markdown: string;
  /** First user message in the conversation — used as the source for the title. */
  firstUserMessage?: string;
  /** Defaults to false; toggled to true when the user adds it to Resources. */
  addedToResources?: boolean;
}

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

function notifyChanged(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(savedDecisionsChangedEvent));
}

function readAll(userId: string): SavedDecision[] {
  if (typeof window === "undefined" || !userId) return [];
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (x): x is SavedDecision =>
        typeof x === "object" &&
        x !== null &&
        typeof (x as SavedDecision).id === "string" &&
        typeof (x as SavedDecision).title === "string" &&
        typeof (x as SavedDecision).markdown === "string" &&
        typeof (x as SavedDecision).createdAt === "string",
    );
  } catch {
    return [];
  }
}

function writeAll(userId: string, rows: SavedDecision[]): void {
  if (typeof window === "undefined" || !userId) return;
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(rows));
  } catch {
    /* quota / private mode — silently ignore */
  }
}

export function clearSavedDecisionsCache(userId?: string): void {
  if (typeof window === "undefined") return;
  try {
    if (userId) {
      window.localStorage.removeItem(storageKey(userId));
    }
    window.localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function listSavedDecisions(userId: string | null | undefined): SavedDecision[] {
  if (!userId) return [];
  return readAll(userId).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

const TITLE_LINE_RE = /^#\s+(.+?)\s*$/m;
const HEADING_OR_LIST_RE = /^[#\->\d.\s*`]+/;
const TITLE_MAX = 70;

function cleanInline(text: string): string {
  return text
    .replace(/`+/g, "")
    .replace(/\*+|_+/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function deriveTitle(markdown: string, firstUserMessage?: string): string {
  const fromUser = firstUserMessage ? cleanInline(firstUserMessage) : "";
  if (fromUser) {
    const sentence = fromUser.split(/(?<=[.!?])\s+/)[0] ?? fromUser;
    const words = sentence.split(/\s+/).slice(0, 10).join(" ");
    const trimmed = words.replace(/[.,;:]+$/, "");
    if (trimmed.length >= 4) {
      return trimmed.length > TITLE_MAX ? `${trimmed.slice(0, TITLE_MAX - 1)}…` : trimmed;
    }
  }

  const m = markdown.match(TITLE_LINE_RE);
  if (m && m[1]) {
    const t = cleanInline(m[1]);
    return t.length > TITLE_MAX ? `${t.slice(0, TITLE_MAX - 1)}…` : t;
  }

  return "Strategic Decision";
}

function pickExcerpt(markdown: string): string {
  const lines = markdown.split(/\r?\n/);
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    if (HEADING_OR_LIST_RE.test(line)) continue;
    return line.replace(/[*_`]+/g, "").slice(0, 220);
  }
  return "A research-backed plan you walked through with HeyBodhi.";
}

export function saveDecision(
  userId: string,
  input: SaveDecisionInput,
): SavedDecision {
  const id = `sd-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const row: SavedDecision = {
    id,
    title: deriveTitle(input.markdown, input.firstUserMessage),
    excerpt: pickExcerpt(input.markdown),
    markdown: input.markdown,
    createdAt: new Date().toISOString(),
    addedToResources: input.addedToResources ?? false,
  };

  const rows = readAll(userId);
  rows.unshift(row);
  writeAll(userId, rows);
  notifyChanged();
  return row;
}

export function updateSavedDecision(
  userId: string,
  id: string,
  patch: Partial<Pick<SavedDecision, "title" | "addedToResources">>,
): SavedDecision | null {
  const rows = readAll(userId);
  const idx = rows.findIndex((r) => r.id === id);
  if (idx === -1) return null;
  const next: SavedDecision = { ...rows[idx]!, ...patch };
  rows[idx] = next;
  writeAll(userId, rows);
  notifyChanged();
  return next;
}

export function setDecisionInResources(
  userId: string,
  id: string,
  addedToResources: boolean,
): SavedDecision | null {
  return updateSavedDecision(userId, id, { addedToResources });
}

export function deleteSavedDecision(userId: string, id: string): void {
  const next = readAll(userId).filter((r) => r.id !== id);
  writeAll(userId, next);
  notifyChanged();
}

function renderHtmlPage(decision: SavedDecision): string {
  const bodyHtml = md.render(decision.markdown);
  const safeTitle = decision.title.replace(/[<>&"]/g, (c) => `&#${c.charCodeAt(0)};`);
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width,initial-scale=1" />
<title>${safeTitle}</title>
<style>
  :root { color-scheme: light; }
  html, body { margin: 0; background: #faf6ee; }
  body {
    font-family: 'Inter', system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
    color: #1c2926;
    padding: 32px;
    line-height: 1.55;
  }
  .doc {
    max-width: 760px;
    margin: 0 auto;
    background: #fff;
    border: 1px solid rgba(0,0,0,0.06);
    border-radius: 16px;
    padding: 32px 36px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.06);
  }
  h1 { font-family: 'Literata', Georgia, serif; font-size: 1.6rem; margin: 0 0 0.5rem; color: #1f3a30; }
  h2 { font-family: 'Literata', Georgia, serif; font-size: 1.15rem; margin: 1.4rem 0 0.4rem; color: #1f3a30; }
  h3 { font-size: 1rem; margin: 1.1rem 0 0.3rem; color: #1f3a30; }
  p, li { font-size: 0.95rem; }
  ul, ol { padding-left: 1.25rem; }
  blockquote {
    border-left: 3px solid #c58c10;
    background: #faf6ee;
    margin: 1rem 0;
    padding: 0.5rem 0.9rem;
    color: #4b3a14;
    border-radius: 0 8px 8px 0;
  }
  code { background: #f1ece1; padding: 0.1rem 0.35rem; border-radius: 4px; font-size: 0.85em; }
  hr { border: 0; border-top: 1px dashed #c9bfa6; margin: 1.5rem 0; }
  .meta { font-size: 0.78rem; color: #6b6356; margin-bottom: 1rem; }
  @media print { body { background: #fff; padding: 0; } .doc { box-shadow: none; border: 0; } }
</style>
</head>
<body>
  <main class="doc">
    <p class="meta">Past Decision · ${new Date(decision.createdAt).toLocaleDateString()}</p>
    ${bodyHtml}
  </main>
</body>
</html>`;
}

function htmlToDataUrl(html: string): string {
  if (typeof btoa === "function") {
    try {
      const utf8 = new TextEncoder().encode(html);
      let binary = "";
      utf8.forEach((b) => {
        binary += String.fromCharCode(b);
      });
      return `data:text/html;charset=utf-8;base64,${btoa(binary)}`;
    } catch {
      /* fall through */
    }
  }
  return `data:text/html;charset=utf-8,${encodeURIComponent(html)}`;
}

const ROUND_ROBIN_PHOTOS = [pathChoicePhoto, compassPhoto];

export function savedDecisionsAsResources(userId: string | null | undefined): Resource[] {
  const rows = listSavedDecisions(userId).filter((r) => r.addedToResources === true);
  return rows.map((row, i) => {
    const html = renderHtmlPage(row);
    const url = htmlToDataUrl(html);
    return {
      id: `saved-decision-${row.id}`,
      title: row.title,
      description: row.excerpt,
      meta: "Read",
      type: "website",
      url,
      photo: ROUND_ROBIN_PHOTOS[i % ROUND_ROBIN_PHOTOS.length] ?? pathChoicePhoto,
      addedDate: row.createdAt,
      category: "Past Decisions",
    };
  });
}

export function savedDecisionAsHtmlDataUrl(decision: SavedDecision): string {
  return htmlToDataUrl(renderHtmlPage(decision));
}

export function getSavedDecisionMarkdown(userId: string, id: string): string | null {
  const stripped = id.replace(/^saved-decision-/, "");
  const row = readAll(userId).find((r) => r.id === stripped);
  return row ? row.markdown : null;
}

export function previewHtmlFromMarkdown(markdown: string, firstUserMessage?: string): string {
  const row: SavedDecision = {
    id: "preview-print",
    title: deriveTitle(markdown, firstUserMessage),
    excerpt: pickExcerpt(markdown),
    markdown,
    createdAt: new Date().toISOString(),
  };
  return renderHtmlPage(row);
}

export function openDocumentationPrintWindow(markdown: string, firstUserMessage?: string): boolean {
  const html = previewHtmlFromMarkdown(markdown, firstUserMessage);
  const w = window.open("", "_blank");
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  window.setTimeout(() => {
    try {
      w.focus();
      w.print();
    } catch {
      /* noop */
    }
  }, 400);
  return true;
}

export function openSavedDecisionInNewTab(decision: SavedDecision): boolean {
  const html = renderHtmlPage(decision);
  const w = window.open("", "_blank");
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}
